import { FC, memo, useMemo, useState } from 'react';
import {
  Avatar,
  AvatarGroup,
  TableRow,
  tailwindOverride,
  Tooltip,
  IconButton,
  Icon,
} from '@getsynapse/design-system';
import { getRequestRoute, navigateToRequest } from 'utils/dynamicRoutes';
import intl from 'react-intl-universal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DeletedUser } from 'Atoms/DeletedUser/DeletedUser';
import {
  ProjectWithPermissions,
  Request,
  UserSummary,
} from 'types/store/normalized';
import { useHistory } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState, useAppThunkDispatch } from 'state/store';
import {
  selectIsUserLd,
  selectOrganizationId,
  selectUserId,
  isUserOrganizationAdmin,
} from 'state/User/userSlice';
import {
  patchRequestStatus,
  buildRequestStatusPatchPayload,
  getApiErrorMessage,
} from 'state/normalized/requests/requestsSlice';
import { RequestStatus } from 'utils/types/request';
import RequestStatusTag from 'Atoms/RequestStatusTag/RequestStatusTag';
import { RequestStatusTableRowCell } from 'Atoms/EditableTableRowCell/RequestStatusTableRowCell';
import { formatDate, formatRequestLearningPriority } from 'utils/formatters';
import {
  REQUEST_STATUS,
  SIDE_PANEL_TYPES,
  USERPILOT_EVENTS,
} from 'utils/constants';
import { REQUEST_STATUSES } from 'utils/constants/request';
import { PilotPagesToTrack } from 'utils/customTypes';
import { Userpilot } from 'userpilot';
import { userSummarySelectors } from 'state/normalized/userSummaries/userSummariesSlice';
import { isUserStatusDisabled } from 'utils/functions';
import { formSummarySelectors } from 'state/normalized/formSummaries/formSummariesSlice';
import { projectSummarySelectors } from 'state/normalized/projectSummaries/projectSummariesSlice';
import { projectPermissionsSelectors } from 'state/normalized/projectPermissions/projectPermissionsSlice';
import { requestPermissionsSelectors } from 'state/normalized/requestPermissions/requestPermissionsSlice';
import LinkedProjectsTooltip from 'Pages/ConsolidatedRequestsListPage/components/LinkedProjectsTooltip/LinkedProjectsTooltip';
import { businessUnitSummarySelectors } from 'state/normalized/businessUnitSummaries/businessUnitSummariesSlice';
import { openPanel } from 'state/SidePanel/sidePanelSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import RequestLink from 'Atoms/RequestLink';
import DeleteRequestModal from '../DeleteRequestModal/DeleteRequestModal';

interface TableRowProps {
  request: Request;
  isOdd?: boolean;
  canSelectRows?: boolean;
  onSelectRow: (rowId: string) => void;
  selected: boolean;
}

const mapToAvatar = (user: UserSummary) => {
  const allInitials = user.name.split(' ').map((n) => n[0]);
  const initial = allInitials[0] + allInitials.splice(-1);
  return {
    imageSrc: user.avatarUrl || '',
    initial,
    name: user.name,
    disabled: isUserStatusDisabled(user.status),
  };
};
// Exported for unit testing. Builds the tooltip shown when the status cell is
// read-only. Statuses that are read-only for everyone (draft, canceled,
// pending_page_approval) get their own dedicated message; do NOT let them fall
// through to ONLY_OWNER_CAN_EDIT, which wrongly implies the owner could edit.
// The ONLY_OWNER_CAN_EDIT fallback interpolates {action}, which MUST be the
// human-readable status label (e.g. "In Review"), never the raw status key
// (e.g. "submitted"). Translate through STATUS_LABEL — keep this in sync with
// RequestStatusTag/RequestStatusTableRowCell, which resolve labels the same way.
export const selectDeclineTooltipMessage = (request: Request) => {
  if (request.isArchived) {
    return intl.get('REQUESTS_LIST_PAGE.DECLINE_TOOLTIP_MESSAGE.ARCHIVED');
  }
  switch (request.status) {
    case REQUEST_STATUS.DRAFT:
      return intl.get('REQUESTS_LIST_PAGE.DECLINE_TOOLTIP_MESSAGE.DRAFT');
    case REQUEST_STATUS.CANCELED:
      return intl.get('REQUESTS_LIST_PAGE.DECLINE_TOOLTIP_MESSAGE.CANCELED');
    case REQUEST_STATUS.PENDING_PAGE_APPROVAL:
      return intl.get(
        'REQUESTS_LIST_PAGE.DECLINE_TOOLTIP_MESSAGE.PENDING_PAGE_APPROVAL'
      );
    default:
      return intl.get(
        'REQUESTS_LIST_PAGE.DECLINE_TOOLTIP_MESSAGE.ONLY_OWNER_CAN_EDIT',
        {
          action: intl.get(
            `REQUESTS_LIST_PAGE.TABLE.STATUS_LABEL.${request.status.toUpperCase()}`
          ),
        }
      );
  }
};
const mapToAvatarGroup = (owners: UserSummary[]) => owners.map(mapToAvatar);

const RequestTableRow: FC<TableRowProps> = ({
  request,
  isOdd,
  canSelectRows = false,
  onSelectRow,
  selected,
}) => {
  const dispatch = useAppThunkDispatch();
  const history = useHistory();

  const {
    consolidatedRequestsPage = false,
    requestStatusInlineEditing = false,
    advancedRequestsCompletedStatus = false,
  } = useFlags();

  const [isDeleteRequestModalOpen, setIsDeleteRequestModalOpen] =
    useState(false);

  const organizationId = useSelector(selectOrganizationId)!;
  const userId = useSelector(selectUserId);
  const isAdmin = useSelector(isUserOrganizationAdmin);
  const isLearningUser = useSelector(selectIsUserLd);
  // Every loaded row stays mounted (the table is not virtualized), so these
  // must select VALUES scoped to this row — never a `(state) => (id) => entity`
  // getter, which returns a new closure per store change and force-renders
  // every row on every action. Array results need `shallowEqual` for the same
  // reason. See the invariant notes on each slice's selectors.
  const owners = useSelector(
    (state: RootState) =>
      userSummarySelectors.selectUserSummariesByIds(state, request.ownerIds),
    shallowEqual
  );
  const requester = useSelector((state: RootState) =>
    userSummarySelectors.selectById(state, request.requesterId || '')
  );
  const form = useSelector((state: RootState) =>
    formSummarySelectors.selectById(state, request.formId)
  );
  const projects = useSelector(
    (state: RootState) =>
      projectSummarySelectors.selectProjectSummariesByIds(
        state,
        request.projectIds
      ),
    shallowEqual
  );
  const projectPermissions = useSelector(
    (state: RootState) =>
      projectPermissionsSelectors.selectProjectPermissionsByIds(
        state,
        request.projectIds
      ),
    shallowEqual
  );
  const businessUnit = useSelector((state: RootState) =>
    businessUnitSummarySelectors.selectById(
      state,
      request.businessTeamId as string
    )
  );
  const requestPermissions = useSelector((state: RootState) =>
    requestPermissionsSelectors.selectById(state, request.id)
  );

  const projectsWithPermissions = useMemo(
    () =>
      projects.map((project) => ({
        ...project,
        permissions: projectPermissions.find(
          (permissions) => permissions.id === project.id
        ),
      })),
    [projectPermissions, projects]
  );

  const canEditStatusInline =
    !request.isArchived &&
    request.status !== REQUEST_STATUS.DRAFT &&
    request.status !== REQUEST_STATUS.CANCELED &&
    // CGP-180: a Completed request is only editable (to move it out of
    // Completed) when the flag is on; with the flag off it is read-only.
    (request.status !== REQUEST_STATUSES.COMPLETED ||
      advancedRequestsCompletedStatus) &&
    // CGP-235: pending_page_approval is a read-only, system-driven gate. Present
    // it read-only (like Draft/Canceled) rather than a clickable cell that opens
    // an empty dropdown; server enforces the block (CGP-124).
    request.status !== REQUEST_STATUSES.PENDING_PAGE_APPROVAL &&
    requestPermissions?.canEditStatusInline === true &&
    (isAdmin || (userId != null && request?.ownerIds?.includes(userId)));
  const isStatusEditDisabled = !canEditStatusInline;

  const requestHref = getRequestRoute(request.id, organizationId, request);

  return (
    <>
      <DeleteRequestModal
        isOpen={isDeleteRequestModalOpen}
        closeModal={() => setIsDeleteRequestModalOpen(false)}
        requestId={request.id}
        requestNumber={request.number}
      />
      <TableRow
        id={request.id}
        canSelectRows={canSelectRows}
        isSelectRowCellSticky
        onSelectRow={onSelectRow}
        selected={selected}
        className={tailwindOverride(
          requestStatusInlineEditing ? '' : 'cursor-pointer',
          isOdd ? 'bg-neutral-lightest-two' : 'bg-neutral-white',
          selected && 'bg-secondary-lightest'
        )}
        key={request.id}
        onClick={
          requestStatusInlineEditing
            ? undefined
            : () => {
                navigateToRequest(history, requestHref, request.isJsonSchema);
              }
        }
        cells={[
          {
            content: request.number,
          },
          {
            content: (
              <div className='w-64 flex'>
                {request.isArchived && (
                  <div>
                    <Icon
                      name='archive'
                      className='mr-2.5 text-primary-dark w-4 h-4'
                      data-testid={`archive-icon-${request.id}`}
                    />
                  </div>
                )}
                <RequestLink
                  href={requestHref}
                  isJsonSchema={request.isJsonSchema}
                  title={request.title}
                  className={
                    requestStatusInlineEditing
                      ? 'truncate cursor-pointer hover:underline hover:text-steel-blue hover:font-semibold'
                      : 'truncate'
                  }
                  onClick={
                    request.isJsonSchema || requestStatusInlineEditing
                      ? (event: React.MouseEvent) => event.stopPropagation()
                      : undefined
                  }
                >
                  {request.title}
                </RequestLink>
              </div>
            ),
          },
          {
            content: requestStatusInlineEditing ? (
              <RequestStatusTableRowCell
                value={request.status}
                isEditable={canEditStatusInline}
                request={request}
                isDisabled={isStatusEditDisabled}
                disabledMessage={
                  isStatusEditDisabled
                    ? selectDeclineTooltipMessage(request)
                    : undefined
                }
                onSave={async (newStatus, params) => {
                  if (!request.id) return;
                  try {
                    const payload = buildRequestStatusPatchPayload(
                      newStatus as RequestStatus,
                      params
                    );
                    await dispatch(
                      patchRequestStatus({
                        requestId: request.id,
                        payload,
                      })
                    ).unwrap();
                    try {
                      Userpilot.track(
                        USERPILOT_EVENTS.REQUEST_STATUS_INLINE_EDITING,
                        {
                          eventOrigin: PilotPagesToTrack.REQUEST_LIST,
                          newValue: newStatus,
                          oldValue: request.status,
                        }
                      );
                    } catch (error) {
                      // eslint-disable-next-line no-console
                      console.error(
                        'Error tracking on Userpilot event for request status inline edit',
                        error
                      );
                    }
                    dispatch(
                      showNotification({
                        notificationVariant: 'success',
                        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
                        notificationMessage: intl.get(
                          'REQUEST_PAGE.NOTIFICATIONS.UPDATE_SUCCESS_MESSAGE'
                        ),
                      })
                    );
                  } catch (error) {
                    // Prefer the v2 API's specific reason (e.g. "Only advanced
                    // requests can be marked as completed.") over the generic
                    // "reload the page" copy, which misrepresents a permanent
                    // validation error as a transient one.
                    dispatch(
                      showNotification({
                        notificationVariant: 'error',
                        notificationTitle: intl.get(
                          'REQUEST_PAGE.UPDATE_ERROR_MESSAGE_TITLE'
                        ),
                        notificationMessage:
                          getApiErrorMessage(error) ??
                          intl.get('REQUEST_PAGE.UPDATE_ERROR_MESSAGE_BODY'),
                      })
                    );
                  }
                }}
              />
            ) : (
              <RequestStatusTag status={request.status} />
            ),
            className: requestStatusInlineEditing
              ? 'cursor-pointer py-0'
              : 'py-3',
          },
          ...(isLearningUser
            ? [
                {
                  content: formatRequestLearningPriority(
                    request.learnAndDevelopmentPriority
                  ),
                },
              ]
            : []),
          {
            content: requester ? (
              <div className='flex items-center'>
                <Avatar
                  {...mapToAvatar(requester)}
                  deactivatedText={intl.get('DEACTIVATED')}
                />
                <span className='ml-2.5'>{requester.name}</span>
              </div>
            ) : (
              <DeletedUser />
            ),
          },
          {
            content:
              owners && owners.length === 1 ? (
                <div className='flex items-center'>
                  <AvatarGroup
                    avatars={mapToAvatarGroup(owners)}
                    deactivatedText={intl.get('DEACTIVATED')}
                  />
                  <span className='ml-2.5'>{owners[0].name}</span>
                </div>
              ) : (
                <AvatarGroup
                  avatars={mapToAvatarGroup(owners)}
                  deactivatedText={intl.get('DEACTIVATED')}
                />
              ),
            className: `font-semibold`,
          },
          {
            content: request.projectIds?.length ? (
              <LinkedProjectsTooltip
                numberOfProjects={`${request.projectIds.length.toString()} ${intl
                  .get('ENTITIES.PROJECT', { num: request.projectIds.length })
                  .toLowerCase()}`}
                projects={projectsWithPermissions as ProjectWithPermissions[]}
              />
            ) : (
              intl.get(
                'REQUESTS_LIST_PAGE.TABLE.LINKED_PROJECTS.NO_LINKED_PROJETS'
              )
            ),
          },
          ...(isLearningUser
            ? [
                {
                  content: (
                    <div className='w-44 truncate'>{businessUnit?.name}</div>
                  ),
                },
              ]
            : []),
          {
            content: <div className='w-44 truncate'>{form?.title}</div>,
          },
          {
            content: formatDate(request.createdAt),
          },
          {
            content: formatDate(request.submittedAt),
          },
          {
            content: formatDate(request.decisionDate),
          },
          {
            content: formatDate(request.updatedAt),
          },
          ...(consolidatedRequestsPage
            ? [
                {
                  content: (
                    <div
                      className='opacity-0 group-hover:opacity-100 flex justify-center'
                      onClick={(event) => event.stopPropagation()}
                    >
                      {
                        <Tooltip
                          openMode='hover1'
                          timeout={0}
                          showPopper
                          position='topCenter'
                          contentProps={{
                            className: 'px-3 py-2 text-sm z-50',
                          }}
                          usePortal
                          trigger={
                            <IconButton
                              name={`${
                                !requestPermissions?.canEdit
                                  ? 'eye'
                                  : 'pencil-outline'
                              }`}
                              iconClassname='pointer-events-none'
                              className='text-base text-neutral-dark p-1 hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
                              data-testid={`quick-edit-button-${request.id}`}
                              onClick={() =>
                                dispatch(
                                  openPanel({
                                    resourceType: SIDE_PANEL_TYPES.REQUEST,
                                    resourceId: request.id!,
                                  })
                                )
                              }
                            />
                          }
                        >
                          {!requestPermissions?.canEdit
                            ? intl.get('SIDE_PANEL.QUICK_VIEW')
                            : intl.get('SIDE_PANEL.QUICK_EDIT')}
                        </Tooltip>
                      }
                      {requestPermissions?.canDelete && (
                        <Tooltip
                          openMode='hover1'
                          timeout={0}
                          showPopper
                          position='topCenter'
                          contentProps={{
                            className: 'px-3 py-2 text-sm z-50',
                          }}
                          usePortal
                          trigger={
                            <IconButton
                              name='trash'
                              iconClassname='pointer-events-none'
                              data-testid={`request-delete-button-${request.id}`}
                              className='text-base ml-2 text-neutral-dark p-1 hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
                              onClick={() => setIsDeleteRequestModalOpen(true)}
                            />
                          }
                        >
                          {intl.get('REQUESTS_LIST_PAGE.TABLE.ACTIONS.DELETE')}
                        </Tooltip>
                      )}
                    </div>
                  ),
                  className: tailwindOverride(
                    'sticky right-0 z-1',
                    isOdd ? 'bg-neutral-lightest-two' : 'bg-neutral-white',
                    selected && 'bg-secondary-lightest'
                  ),
                },
              ]
            : []),
        ]}
      />
    </>
  );
};

/**
 * Memoized because the requests list keeps every loaded row mounted. This only
 * holds while the row's props stay referentially stable — in particular
 * `onSelectRow`, which RequestsTable keeps stable via useCallback — and while
 * the row's selectors return values rather than freshly-built closures.
 */
export default memo(RequestTableRow);
